import classnames from "classnames";
import debounce from "lodash/debounce";
import PropTypes from "prop-types";
import React, { forwardRef, useEffect, useRef, useState } from "react";

import useOutputReportStore from "../../store/OutputReport.store";
import { getOutputType } from "../../utils/helpers/dataTable";
import { dataTableCopy } from "../../utils/resources/dataTableCopy";
import Slider from "../Slider";
import DataTableHeadings from "./DataTableHeadings";
import DataTableRows from "./DataTableRows";

import "./dataTable.scss";

const scenarioColumns = [
  "Minimize Tax Liability",
  "Balance Tax Liability & Tracking Error",
  "Minimize Tracking Error",
  "Target Portfolio",
  "Target Portfolio*",
  "Target Portfolio**",
  "Target Portfolio1",
];

const highlightColumns = [
  "Maintain and Reinvest Proceeds",
  "Maintain And Reinvest Proceeds",
  "Existing Portfolio (Maintain & Reinvest Proceeds)",
  "Existing Portfolio*",
];

const TableMarkup = forwardRef(
  (
    {
      variation,
      tableCaption,
      tableRowHeaderKey,
      tableRowKeys,
      tableColumns,
      tableDataTypes,
      formatCellDataBy,
      name,
      tableHeight,
      hasProfitLabel,
    },
    ref
  ) => (
    <div className="dataTable__wrapper">
      <table
        className={classnames("table", "dataTable__table", {
          [`dataTable--${variation}`]: variation,
          [`dataTable--cols-${tableColumns.length}`]: tableColumns.length > 0,
        })}
        ref={ref}
        style={{ "--table-height": `${tableHeight}` }}
      >
        <caption className="visually-hidden">{tableCaption}</caption>
        <thead className="dataTable__head">
          <DataTableHeadings
            name={name}
            variation={variation}
            tableColumns={tableColumns}
            tableRowHeaderKey={tableRowHeaderKey}
            scenarioColumns={scenarioColumns}
            highlightColumns={highlightColumns}
          />
        </thead>
        <tbody className="dataTable__body">
          <DataTableRows
            name={name}
            variation={variation}
            tableRowKeys={tableRowKeys}
            tableColumns={tableColumns}
            tableDataTypes={tableDataTypes}
            scenarioColumns={scenarioColumns}
            highlightColumns={highlightColumns}
            formatCellDataBy={formatCellDataBy}
            hasProfitLabel={hasProfitLabel} // temp
          />
        </tbody>
      </table>
    </div>
  )
);

TableMarkup.propTypes = {
  variation: PropTypes.oneOf(["basic", "stylized", "max"]),
  tableCaption: PropTypes.string,
  tableRowHeaderKey: PropTypes.string,
  tableRowKeys: PropTypes.array,
  tableColumns: PropTypes.array,
  tableDataTypes: PropTypes.object,
  formatCellDataBy: PropTypes.oneOf(["row", "column"]),
  name: PropTypes.string,
  tableHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

TableMarkup.defaultProps = {
  variation: "basic",
  tableRowKeys: [],
  tableColumns: [],
  tableDataTypes: {},
  formatCellDataBy: "row",
};

const DataTable = ({
  reportType,
  name,
  footnote,
  info,
  definitions,
  ...restProps
}) => {
  const tableRef = useRef();
  const tableCardRef = useRef();
  const [tableHeight, setTableHeight] = useState(0);
  const [enableScroll, setEnableScroll] = useState(false);
  const paddingOffset = 30;
  const data = useOutputReportStore((state) => state.data);
  const { assetType } = getOutputType(data);

  const handleTableSettings = () => {
    let tHeight = tableRef.current?.clientHeight;

    if (reportType === "transition" && assetType === "equity") {
      tHeight -= 64;
    } else if (reportType === "transition") {
      tHeight -= 48;
    }

    setTableHeight(`${tHeight}px`);

    setEnableScroll(
      tableRef.current?.clientWidth >
        tableCardRef.current?.clientWidth - paddingOffset
    );
  };
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (tableRef.current) {
      window.addEventListener("resize", debounce(handleTableSettings, 500));
      return () => {
        window.removeEventListener(
          "resize",
          debounce(handleTableSettings, 500)
        );
      };
    }
  }, [tableRef]);

  useEffect(() => {
    // Make sure that the page and its fonts has loaded
    // before getting the final height of the table
    if (document.readyState === "complete") {
      handleTableSettings();
    } else {
      window.addEventListener("load", handleTableSettings);
      // Remove the event listener when component unmounts
      return () => window.removeEventListener("load", handleTableSettings);
    }
    // dispatch resize it once mounted
    window.dispatchEvent(new Event("resize"));
  }, [tableHeight]);
  /* eslint-enable react-hooks/exhaustive-deps */

  return (
    <div
      className={classnames("dataTable", {
        [`dataTable--${name}`]: name,
        [`dataTable--${reportType}`]: reportType,
      })}
    >
      <div className="dataTable-card" ref={tableCardRef}>
        {info && <div className="dataTable__info">{info}</div>}
        {enableScroll ? (
          <Slider className="dataTable__slider">
            <TableMarkup
              {...restProps}
              name={name}
              tableHeight={tableHeight}
              ref={tableRef}
            />
          </Slider>
        ) : (
          <TableMarkup
            {...restProps}
            name={name}
            tableHeight={tableHeight}
            ref={tableRef}
          />
        )}
      </div>
      {definitions && (
        <div className="dataTable__definitions">{definitions}</div>
      )}
      <div className="dataTable__footnote">
        {footnote}
        {dataTableCopy?.legalCopy && (
          <p className="dataTable__footnote-legal">{dataTableCopy.legalCopy}</p>
        )}
      </div>
    </div>
  );
};

DataTable.propTypes = {
  reportType: PropTypes.string,
  name: PropTypes.string,
  footnote: PropTypes.node,
  info: PropTypes.node,
  definitions: PropTypes.node,
};

DataTable.defaultProps = {
  reportType: "transition",
};

export default DataTable;
